import { Component, OnInit } from '@angular/core';

/**
 * Full-page lockout shown to end users when the auth backend returns a 403
 * with a LICENSE_* code (REVOKED / EXPIRED / SUSPENDED / UNAVAILABLE).
 *
 * End-user variant of the admin-ui lockout — intentionally minimal:
 *   - No /internal/license/refresh call (anonymous users have no bearer token
 *     for that endpoint).
 *   - No auto-poll (a stranded user typically reloads or closes the tab).
 *   - No "sign out" — they were never signed in.
 *   - Copy avoids tenant/license jargon; the end user only needs to know the
 *     service can't accept them right now and who to contact.
 *
 * The interceptor stashes the reason in sessionStorage before redirecting
 * here. UNAVAILABLE is presented as transient ("try again"); the other three
 * are presented as something only the customer's administrator can fix.
 */
@Component({
  selector: 'app-license-locked',
  templateUrl: './license-locked.component.html',
  styleUrls: ['./license-locked.component.scss'],
})
export class LicenseLockedComponent implements OnInit {
  reason: string = 'LICENSE_UNAVAILABLE';
  licenseDetails: any = null;

  ngOnInit(): void {
    const stashedReason = sessionStorage.getItem('license_lockout_reason');
    const stashedData = sessionStorage.getItem('license_lockout_data');
    if (stashedReason) this.reason = stashedReason;
    if (stashedData) {
      try {
        this.licenseDetails = JSON.parse(stashedData);
      } catch {
        this.licenseDetails = null;
      }
    }
  }

  tryAgain(): void {
    sessionStorage.removeItem('license_lockout_reason');
    sessionStorage.removeItem('license_lockout_data');
    window.location.reload();
  }

  // ─── presentation ───────────────────────────────────────────────────────

  get heading(): string {
    switch (this.reason) {
      case 'LICENSE_REVOKED':
      case 'LICENSE_EXPIRED':
      case 'LICENSE_SUSPENDED':
        return 'Service Unavailable';
      case 'LICENSE_UNAVAILABLE':
      default:
        return 'We could not reach the service';
    }
  }

  get explanation(): string {
    switch (this.reason) {
      case 'LICENSE_REVOKED':
      case 'LICENSE_EXPIRED':
      case 'LICENSE_SUSPENDED':
        return 'Your organization\'s subscription is currently inactive, so sign-in and registration are paused. Please contact your administrator to restore access.';
      case 'LICENSE_UNAVAILABLE':
      default:
        return 'There was a temporary problem verifying your organization\'s subscription. This is usually fixed in a few moments — please try again.';
    }
  }

  get isTransient(): boolean {
    return this.reason === 'LICENSE_UNAVAILABLE';
  }

  get supportEmail(): string {
    return this.licenseDetails?.support_contact?.email ?? '';
  }
}
